import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Progress,
  Tab,
  TabList,
  Tabs,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Badge,
  TableContainer,
  useToast,
  FormLabel,
  Textarea,
  Checkbox,
  VStack,
  HStack,
  Stack,
} from '@chakra-ui/react';
import { getData, postData, putData } from '../../utils/httpClient';
import ConfirmModal from '../modal/ConfirmModal';
import {
  buildControlTemplateFromProducts,
  buildControlSnapshot,
  createInitialSessionState,
  getSessionStats,
  buildCategoryDisplayRows,
  pruneSessionItems,
  normalizeControlTemplate,
  formatCompactProductCode,
} from '../../utils/stockControlUtils';

const STATUS_META = {
  pending: { label: 'Pendiente', color: 'gray' },
  ok: { label: 'OK', color: 'green' },
  adjusted: { label: 'Ajustado', color: 'orange' },
};

const StockControlPanel = ({
  sessionId: initialSessionId,
  viewOnly = false,
  onSessionChange,
  onExit,
}) => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [template, setTemplate] = useState(null);
  const [session, setSession] = useState(null);
  const [controlSessionId, setControlSessionId] = useState(initialSessionId || null);
  const [sessionStatus, setSessionStatus] = useState(initialSessionId ? null : 'draft');
  const [filter, setFilter] = useState('');
  const [tabIndex, setTabIndex] = useState(0);
  const [showPendingOnly, setShowPendingOnly] = useState(false);

  const isReadOnly = viewOnly || sessionStatus === 'finalized';
  const isDraft = !viewOnly && (sessionStatus === 'draft' || sessionStatus === null);

  const [okTarget, setOkTarget] = useState(null);
  const [okReason, setOkReason] = useState('Control físico conforme');
  const [adjustTarget, setAdjustTarget] = useState(null);
  const [adjustQty, setAdjustQty] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [batchOkCategory, setBatchOkCategory] = useState(null);
  const [batchOkReason, setBatchOkReason] = useState('Control físico conforme');
  const [confirmKind, setConfirmKind] = useState(null);
  const [pendingFinishCount, setPendingFinishCount] = useState(0);

  const mergeSessionIntoTemplate = (built, savedSession, savedTemplate) => {
    const next = createInitialSessionState(built);
    if (!savedSession?.items) return next;

    const byProductId = new Map();
    (savedTemplate?.categories || []).forEach((cat) => {
      (cat.rows || []).forEach((row) => {
        const st = savedSession.items[row.id];
        if (st && row.productId) byProductId.set(row.productId, st);
      });
    });

    Object.keys(next.items).forEach((id) => {
      const productId = Number(String(id).replace('product-', ''));
      const saved =
        savedSession.items[id] ||
        (Number.isFinite(productId) ? byProductId.get(productId) : null);
      if (saved) next.items[id] = { ...next.items[id], ...saved };
    });

    return pruneSessionItems(built, {
      ...next,
      startedAt: savedSession.startedAt || next.startedAt,
      finishedAt: savedSession.finishedAt || null,
    });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const productsRes = await getData('/stock/products-summary');
      const products = productsRes?.payload?.products || [];
      const built = buildControlTemplateFromProducts(products);

      if (controlSessionId) {
        const sessRes = await getData(`/stock/control-sessions/${controlSessionId}`);
        const row = sessRes?.payload?.session;
        if (!row?.snapshot) throw new Error('Sin datos');
        setSessionStatus(row.status);
        const snapTemplate = row.snapshot.template;
        const snapSession = row.snapshot.session;

        if (viewOnly || row.status === 'finalized') {
          const tpl =
            snapTemplate?.categories?.length > 0
              ? normalizeControlTemplate(snapTemplate)
              : built;
          setTemplate(tpl);
          setSession(
            pruneSessionItems(
              tpl,
              snapSession || createInitialSessionState(tpl),
            ),
          );
        } else {
          setTemplate(built);
          setSession(
            mergeSessionIntoTemplate(built, snapSession, snapTemplate),
          );
        }
      } else {
        setSessionStatus('draft');
        setTemplate(built);
        setSession(createInitialSessionState(built));
      }
    } catch {
      toast({
        title: 'Error al cargar control de stock',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  }, [toast, controlSessionId, viewOnly]);

  useEffect(() => {
    setControlSessionId(initialSessionId || null);
  }, [initialSessionId]);

  useEffect(() => {
    load();
  }, [load]);

  const buildSnapshotPayload = (opts) => buildControlSnapshot(template, session, opts);

  const persistDraft = async () => {
    if (!template || !session || isReadOnly) return null;
    setSaving(true);
    try {
      if (controlSessionId) {
        // Ya existe la sesión: el backend ya tiene el catálogo, no hace falta reenviarlo.
        const snapshot = buildSnapshotPayload({ includeTemplate: false });
        const res = await putData(`/stock/control-sessions/${controlSessionId}`, {
          snapshot,
        });
        return res?.payload?.session;
      }
      const snapshot = buildSnapshotPayload();
      const res = await postData('/stock/control-sessions', { snapshot });
      const created = res?.payload?.session;
      if (created?.id) {
        setControlSessionId(created.id);
        setSessionStatus('draft');
        onSessionChange?.(created.id);
      }
      return created;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    try {
      await persistDraft();
      toast({
        title: 'Borrador guardado',
        status: 'success',
        duration: 2500,
      });
    } catch {
      toast({
        title: 'No se pudo guardar el borrador',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const requestDiscard = () => {
    if (!controlSessionId) {
      onExit?.();
      return;
    }
    setConfirmKind('discard');
  };

  const confirmDiscard = async () => {
    setConfirmKind(null);
    if (!controlSessionId) return;
    setSaving(true);
    try {
      await postData(`/stock/control-sessions/${controlSessionId}/discard`);
      toast({ title: 'Borrador descartado', status: 'info', duration: 2500 });
      onExit?.();
    } catch {
      toast({
        title: 'No se pudo descartar',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setSaving(false);
    }
  };

  const stats = useMemo(() => {
    if (!template || !session) return { total: 0, checked: 0, percent: 0 };
    return getSessionStats(template, session);
  }, [template, session]);

  const categoryBreakdown = useMemo(() => {
    if (!template?.categories?.length) return [];
    return template.categories.map((cat) => {
      const catStats = getSessionStats({ categories: [cat] }, session);
      const pending = catStats.total - catStats.checked;
      return {
        id: cat.id,
        name: cat.name,
        total: catStats.total,
        checked: catStats.checked,
        pending,
      };
    });
  }, [template, session]);

  const setRowSession = (rowId, patch) => {
    setSession((prev) => ({
      ...prev,
      items: {
        ...prev.items,
        [rowId]: { ...prev.items[rowId], ...patch },
      },
    }));
  };

  const refreshProductRow = async (productId) => {
    const res = await getData('/stock/products-summary');
    const product = (res?.payload?.products || []).find((p) => p.productId === productId);
    if (!product || !template) return;

    setTemplate((prev) => {
      const categories = prev.categories.map((cat) => ({
        ...cat,
        rows: cat.rows.map((row) => {
          if (row.productId !== productId) return row;
          const qty = Number(product.quantity) || 0;
          return {
            ...row,
            systemStock: qty,
            displayStock: qty,
            stockSource: product.stockSource,
            lots: product.lots || [],
            primaryLotId: product.primaryLotId,
            zone: product.zone ?? '',
            shelfLetter: product.shelfLetter ?? '',
            shelfNumber: product.shelfNumber ?? '',
          };
        }),
      }));
      return { ...prev, categories };
    });

    setRowSession(`product-${productId}`, {
      physicalCount: String(Number(product.quantity) || 0),
    });
  };

  const submitOk = async () => {
    if (!okTarget) return;
    const reason = okReason.trim();
    if (reason.length < 3) {
      toast({ title: 'Indique un motivo (mín. 3 caracteres)', status: 'warning', duration: 3000 });
      return;
    }
    setActionLoading(true);
    try {
      await postData(`/stock/products/${okTarget.productId}/control-ok`, { reason });
      setRowSession(okTarget.id, { status: 'ok', reason });
      await refreshProductRow(okTarget.productId);
      toast({ title: 'Marcado como OK', status: 'success', duration: 2500 });
      setOkTarget(null);
    } catch (e) {
      toast({
        title: e?.response?.data?.message || 'Error al registrar OK',
        status: 'error',
        duration: 4000,
      });
    } finally {
      setActionLoading(false);
    }
  };

  const submitAdjust = async () => {
    if (!adjustTarget) return;
    const reason = adjustReason.trim();
    const qty = Number(adjustQty);
    if (reason.length < 3) {
      toast({ title: 'El motivo es obligatorio', status: 'warning', duration: 3000 });
      return;
    }
    if (Number.isNaN(qty) || qty < 0) {
      toast({ title: 'Cantidad inválida', status: 'warning', duration: 3000 });
      return;
    }
    setActionLoading(true);
    try {
      const body = { quantity: qty, reason };
      if (!adjustTarget.lots?.length) {
        body.zone = adjustTarget.zone || undefined;
        body.shelfLetter = adjustTarget.shelfLetter || undefined;
        body.shelfNumber = adjustTarget.shelfNumber || undefined;
      }
      await postData(`/stock/products/${adjustTarget.productId}/control-adjust`, body);
      setRowSession(adjustTarget.id, {
        status: 'adjusted',
        reason,
        physicalCount: String(qty),
      });
      await refreshProductRow(adjustTarget.productId);
      toast({ title: 'Stock ajustado', status: 'success', duration: 2500 });
      setAdjustTarget(null);
    } catch (e) {
      toast({
        title: e?.response?.data?.message || 'Error al ajustar',
        status: 'error',
        duration: 4000,
      });
    } finally {
      setActionLoading(false);
    }
  };

  const submitBatchOk = async () => {
    if (!batchOkCategory) return;
    const reason = batchOkReason.trim();
    if (reason.length < 3) {
      toast({ title: 'Indique un motivo', status: 'warning', duration: 3000 });
      return;
    }
    const pending = batchOkCategory.rows.filter(
      (row) => session?.items[row.id]?.status === 'pending',
    );
    if (pending.length === 0) {
      setBatchOkCategory(null);
      return;
    }
    setActionLoading(true);
    try {
      for (const row of pending) {
        await postData(`/stock/products/${row.productId}/control-ok`, { reason });
        setRowSession(row.id, { status: 'ok', reason });
      }
      await load();
      toast({
        title: `${pending.length} producto(s) marcados OK`,
        status: 'success',
        duration: 3000,
      });
      setBatchOkCategory(null);
    } catch (e) {
      toast({
        title: e?.response?.data?.message || 'Error en lote OK',
        status: 'error',
        duration: 4000,
      });
    } finally {
      setActionLoading(false);
    }
  };

  const openAdjust = (row) => {
    setAdjustTarget(row);
    setAdjustQty(String(row.systemStock ?? 0));
    setAdjustReason('');
  };

  const requestFinish = () => {
    if (isReadOnly) return;
    const pending = stats.total - stats.checked;
    if (pending > 0) {
      setPendingFinishCount(pending);
      setConfirmKind('finish');
      return;
    }
    runFinalize();
  };

  const runFinalize = async () => {
    if (isReadOnly) return;
    const finishedSession = {
      ...session,
      finishedAt: new Date().toISOString(),
    };
    setSession(finishedSession);
    setSaving(true);
    try {
      let id = controlSessionId;
      if (!id) {
        // Creación: el backend todavía no tiene el catálogo, hay que mandarlo esta vez.
        const snapshot = buildControlSnapshot(template, finishedSession);
        const created = await postData('/stock/control-sessions', { snapshot });
        id = created?.payload?.session?.id;
        if (id) {
          setControlSessionId(id);
          onSessionChange?.(id);
        }
      }
      if (!id) throw new Error('Sin id de sesión');
      // La sesión ya existe (recién creada o de antes): el catálogo ya está guardado,
      // no hace falta reenviarlo.
      const sessionOnlySnapshot = buildControlSnapshot(template, finishedSession, {
        includeTemplate: false,
      });
      await postData(`/stock/control-sessions/${id}/finalize`, { snapshot: sessionOnlySnapshot });
      setSessionStatus('finalized');
      toast({
        title: 'Control finalizado',
        description: 'Quedó registrado. Exporte el reporte desde Inventario → Controles de stock.',
        status: 'success',
        duration: 4000,
      });
    } catch (error) {
      toast({
        title: 'No se pudo finalizar el control',
        description: error?.response?.data?.message || error?.message || undefined,
        status: 'error',
        duration: 4000,
      });
    } finally {
      setSaving(false);
    }
  };

  const confirmFinish = () => {
    setConfirmKind(null);
    runFinalize();
  };

  const filterText = filter.trim().toLowerCase();

  const filterRow = (row) => {
    if (showPendingOnly && row.status !== 'pending') return false;
    if (!filterText) return true;
    const hay = [
      row.model,
      row.description,
      row.productCode,
      row.code1,
      row.code2,
      row.code3,
      row.code4,
      row.shelfLetter,
      row.shelfNumber,
      row.zone,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return hay.includes(filterText);
  };

  const getCategoryRows = (category, forPrint = false) =>
    buildCategoryDisplayRows(category, session).filter((row) =>
      forPrint ? true : filterRow(row),
    );

  const renderCategoryCards = (category) => {
    const rows = getCategoryRows(category);

    return (
      <VStack
        spacing={3}
        align="stretch"
        display={{ base: 'flex', lg: 'none' }}
        className="stock-control-cards"
      >
        {rows.length === 0 ? (
          <Text textAlign="center" color="gray.500" py={6}>
            Sin resultados
          </Text>
        ) : (
          rows.map((row) => {
            const meta = STATUS_META[row.status] || STATUS_META.pending;
            const rowBg =
              row.status === 'ok'
                ? 'green.50'
                : row.status === 'adjusted'
                  ? 'orange.50'
                  : 'white';
            return (
              <Box
                key={row.id}
                p={4}
                borderWidth="1px"
                borderRadius="md"
                borderColor="gray.200"
                bg={rowBg}
              >
                <Flex justify="space-between" align="flex-start" gap={2} mb={2}>
                  <Badge colorScheme={meta.color} fontSize="xs">
                    {meta.label}
                  </Badge>
                  <Text fontSize="sm" fontWeight="bold">
                    Stock: {row.systemStock ?? 0}
                  </Text>
                </Flex>
                <Text fontSize="md" fontWeight="semibold" mb={1}>
                  {row.description}
                </Text>
                <Text fontSize="xs" fontFamily="mono" color="gray.600" mb={2}>
                  Cód.{' '}
                  {row.compactCode ||
                    formatCompactProductCode(row.productCode) ||
                    '—'}
                </Text>
                <Box fontSize="xs" color="gray.600">
                  <Text fontWeight="semibold">Ubicación</Text>
                  <Text>
                    Repisa {row.shelfLetter || '—'} · Est. {row.shelfNumber || '—'}
                  </Text>
                  <Text>Zona: {row.zone || '—'}</Text>
                </Box>
                {row.reason && (
                  <Text fontSize="xs" color="gray.500" mt={2}>
                    {row.reason}
                  </Text>
                )}
                {row.status === 'pending' && !isReadOnly && (
                  <Stack direction="row" spacing={2} mt={4}>
                    <Button
                      flex={1}
                      size="md"
                      colorScheme="green"
                      variant="outline"
                      isDisabled={actionLoading || isReadOnly}
                      onClick={() => {
                        setOkTarget(row);
                        setOkReason('Control físico conforme');
                      }}
                    >
                      OK
                    </Button>
                    <Button
                      flex={1}
                      size="md"
                      colorScheme="orange"
                      variant="outline"
                      isDisabled={actionLoading || isReadOnly}
                      onClick={() => openAdjust(row)}
                    >
                      Ajustar
                    </Button>
                  </Stack>
                )}
              </Box>
            );
          })
        )}
      </VStack>
    );
  };

  const renderCategoryTable = (category, forPrint = false) => {
    const rows = getCategoryRows(category, forPrint);

    return (
      <TableContainer
        borderWidth="1px"
        borderRadius="md"
        maxH={{ base: 'none', lg: '62vh' }}
        overflowY={{ base: 'visible', lg: 'auto' }}
        display={{ base: 'none', lg: 'block' }}
        className="stock-control-table"
      >
        <Table size="sm" variant="simple">
          <Thead position="sticky" top={0} bg="gray.100" zIndex={1}>
            <Tr>
              <Th w="90px">Estado</Th>
              <Th minW="220px">Descripción</Th>
              <Th w="120px">Código</Th>
              <Th w="52px">Repisa</Th>
              <Th w="58px">Estante</Th>
              <Th w="64px">Zona</Th>
              <Th w="52px" isNumeric>
                Stock
              </Th>
              {!forPrint && <Th w="140px">Acciones</Th>}
            </Tr>
          </Thead>
          <Tbody>
            {rows.length === 0 ? (
              <Tr>
                <Td colSpan={forPrint ? 8 : 9} textAlign="center" color="gray.500">
                  Sin resultados
                </Td>
              </Tr>
            ) : (
              rows.map((row) => {
                const meta = STATUS_META[row.status] || STATUS_META.pending;
                const rowBg =
                  row.status === 'ok'
                    ? 'green.50'
                    : row.status === 'adjusted'
                      ? 'orange.50'
                      : undefined;
                return (
                    <Tr key={row.id} bg={rowBg} _hover={{ bg: rowBg || 'gray.50' }}>
                      <Td>
                        <Badge colorScheme={meta.color} fontSize="xs">
                          {meta.label}
                        </Badge>
                        {row.reason && (
                          <Text fontSize="xs" color="gray.500" mt={1} noOfLines={2}>
                            {row.reason}
                          </Text>
                        )}
                      </Td>
                      <Td fontSize="sm" whiteSpace="normal" wordBreak="break-word">
                        {row.description}
                      </Td>
                      <Td fontFamily="mono" fontSize="xs">
                        {row.compactCode ||
                          formatCompactProductCode(row.productCode) ||
                          '—'}
                      </Td>
                      <Td fontFamily="mono" fontSize="xs">
                        {row.shelfLetter || '—'}
                      </Td>
                      <Td fontFamily="mono" fontSize="xs">
                        {row.shelfNumber || '—'}
                      </Td>
                      <Td fontSize="xs">{row.zone || '—'}</Td>
                      <Td isNumeric>
                        {row.systemStock ?? 0}
                      </Td>
                      {!forPrint && (
                        <Td>
                          <HStack spacing={1}>
                            <Button
                              size="xs"
                              colorScheme="green"
                              variant="outline"
                              isDisabled={
                                row.status !== 'pending' || actionLoading || isReadOnly
                              }
                              onClick={() => {
                                setOkTarget(row);
                                setOkReason('Control físico conforme');
                              }}
                            >
                              OK
                            </Button>
                            <Button
                              size="xs"
                              colorScheme="orange"
                              variant="outline"
                              isDisabled={
                                row.status !== 'pending' || actionLoading || isReadOnly
                              }
                              onClick={() => openAdjust(row)}
                            >
                              Ajustar
                            </Button>
                          </HStack>
                        </Td>
                      )}
                    </Tr>
                );
              })
            )}
          </Tbody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <>
      <Box className="stock-control-page">
        <Box
          mb={4}
          p={3}
          bg="gray.50"
          borderRadius="md"
          borderWidth="1px"
          borderColor="gray.200"
          className="no-print"
        >
          <Flex align="center" gap={3} flexWrap="wrap">
            <Box flex={1} minW="160px">
              <Progress
                value={stats.percent}
                size="sm"
                colorScheme="green"
                borderRadius="md"
              />
            </Box>
            <Text fontSize="sm" fontWeight="medium" whiteSpace="nowrap">
              Revisados: {stats.checked} / {stats.total}
            </Text>
          </Flex>
          {categoryBreakdown.length > 0 && (
            <Text fontSize="xs" color="gray.600" mt={2}>
              {categoryBreakdown.length > 1 ? (
                <>
                  {stats.total} productos en {categoryBreakdown.length} categorías:{' '}
                  {categoryBreakdown.map((c) => `${c.name} (${c.total})`).join(' · ')}.
                  Revisá todas las pestañas.
                </>
              ) : (
                <>
                  {stats.total} producto{stats.total !== 1 ? 's' : ''} en{' '}
                  {categoryBreakdown[0].name}.
                </>
              )}
            </Text>
          )}
        </Box>

        {loading || !template || !session ? (
          <Text>Cargando listado…</Text>
        ) : template.categories.length === 0 ? (
          <Text color="gray.500">Sin productos</Text>
        ) : (
          <>
            <Flex
              gap={3}
              mb={4}
              flexWrap="wrap"
              align="center"
              className="no-print"
            >
              <Input
                size="sm"
                flex={{ base: '1 1 100%', sm: '1 1 auto' }}
                maxW={{ base: '100%', sm: '320px' }}
                placeholder="Buscar modelo, producto, código…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
              <Checkbox
                isChecked={showPendingOnly}
                onChange={(e) => setShowPendingOnly(e.target.checked)}
              >
                Solo pendientes
              </Checkbox>
              {isReadOnly && sessionStatus === 'finalized' && (
                <Badge colorScheme="green">Control finalizado (solo lectura)</Badge>
              )}
              {isReadOnly && viewOnly && sessionStatus === 'draft' && (
                <Badge colorScheme="blue">Vista de borrador (solo lectura)</Badge>
              )}
              {isDraft && controlSessionId && (
                <Badge colorScheme="orange">Borrador — edición</Badge>
              )}
            </Flex>

            <Box display="none" className="print-all-categories">
              <Heading size="md" textAlign="center" mb={2}>
                {template.title}
              </Heading>
              {template.categories.map((cat) => (
                <Box key={`print-${cat.id}`} mb={8} className="print-category">
                  <Heading size="sm" mb={2}>
                    {cat.name}
                  </Heading>
                  {renderCategoryTable(cat, true)}
                </Box>
              ))}
            </Box>

            <Tabs
              index={tabIndex}
              onChange={setTabIndex}
              variant="enclosed"
              colorScheme="gray"
            >
              <TabList
                flexWrap="nowrap"
                overflowX="auto"
                overflowY="hidden"
                position="relative"
                zIndex={2}
                bg="gray.50"
                className="no-print"
                sx={{
                  '&::-webkit-scrollbar': { height: '6px' },
                  scrollbarWidth: 'thin',
                }}
              >
                {template.categories.map((cat, idx) => {
                  const catStats = getSessionStats({ categories: [cat] }, session);
                  const pending = catStats.total - catStats.checked;
                  const isActive = idx === tabIndex;
                  return (
                    <Tab key={cat.id} fontSize="sm" flexShrink={0} whiteSpace="nowrap">
                      {cat.name}
                      <Badge
                        ml={2}
                        fontSize="xs"
                        colorScheme={
                          !isActive && pending > 0 ? 'orange' : 'gray'
                        }
                      >
                        {catStats.checked}/{catStats.total}
                      </Badge>
                    </Tab>
                  );
                })}
              </TabList>
            </Tabs>

            <Box pt={4} className="no-print">
              {template.categories.map((cat, idx) =>
                idx === tabIndex ? (
                  <Box key={cat.id}>
                    {!isReadOnly && (
                      <Flex justify="flex-end" mb={3}>
                        <Button
                          size="sm"
                          variant="outline"
                          w={{ base: '100%', lg: 'auto' }}
                          isDisabled={actionLoading}
                          onClick={() => {
                            setBatchOkCategory(cat);
                            setBatchOkReason('Control físico conforme');
                          }}
                        >
                          Marcar pendientes OK (categoría)
                        </Button>
                      </Flex>
                    )}
                    {renderCategoryCards(cat)}
                    {renderCategoryTable(cat)}
                  </Box>
                ) : null,
              )}
            </Box>

            {!isReadOnly && (
              <Flex
                gap={2}
                flexWrap="wrap"
                mt={6}
                pt={4}
                borderTopWidth="1px"
                borderColor="gray.200"
                position={{ base: 'sticky', md: 'static' }}
                bottom={0}
                bg="gray.50"
                zIndex={5}
                className="no-print"
                pb={{ base: 2, md: 0 }}
              >
                <Button
                  size="sm"
                  flex={{ base: '1 1 45%', sm: '0 0 auto' }}
                  variant="outline"
                  onClick={handleSaveDraft}
                  isLoading={saving}
                  isDisabled={!session}
                >
                  Guardar borrador
                </Button>
                <Button
                  size="sm"
                  flex={{ base: '1 1 45%', sm: '0 0 auto' }}
                  colorScheme="green"
                  onClick={requestFinish}
                  isLoading={saving}
                  isDisabled={!session}
                >
                  Finalizar control
                </Button>
                <Button
                  size="sm"
                  flex={{ base: '1 1 100%', sm: '0 0 auto' }}
                  variant="ghost"
                  colorScheme="red"
                  onClick={requestDiscard}
                  isDisabled={saving}
                >
                  {controlSessionId ? 'Descartar borrador' : 'Salir'}
                </Button>
              </Flex>
            )}
          </>
        )}
      </Box>

      <Modal
        isOpen={!!okTarget}
        onClose={() => setOkTarget(null)}
        isCentered
        size={{ base: 'full', md: 'md' }}
        scrollBehavior="inside"
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">Confirmar stock OK</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" mb={3}>
              <strong>{okTarget?.description}</strong>
              <br />
              Cód. pieza:{' '}
              <Text as="span" fontFamily="mono">
                {okTarget?.compactCode ||
                  formatCompactProductCode(okTarget?.productCode) ||
                  '—'}
              </Text>
              <br />
              Stock sistema: {okTarget?.systemStock ?? 0}
            </Text>
            <FormLabel fontSize="sm">
              Motivo <Text as="span" color="red.500">*</Text>
            </FormLabel>
            <Textarea
              size="sm"
              rows={2}
              value={okReason}
              onChange={(e) => setOkReason(e.target.value)}
            />
          </ModalBody>
          <ModalFooter gap={2}>
            <Button size="sm" variant="ghost" onClick={() => setOkTarget(null)}>
              Cancelar
            </Button>
            <Button
              size="sm"
              colorScheme="green"
              isLoading={actionLoading}
              onClick={submitOk}
            >
              Confirmar OK
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={!!adjustTarget}
        onClose={() => setAdjustTarget(null)}
        isCentered
        size={{ base: 'full', md: 'md' }}
        scrollBehavior="inside"
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">Ajustar stock</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" mb={3}>
              <strong>{adjustTarget?.description}</strong>
              <br />
              Cód. pieza:{' '}
              <Text as="span" fontFamily="mono">
                {adjustTarget?.compactCode ||
                  formatCompactProductCode(adjustTarget?.productCode) ||
                  '—'}
              </Text>
              <br />
              Stock actual en sistema: <strong>{adjustTarget?.systemStock ?? 0}</strong>
            </Text>
            <FormLabel fontSize="sm">Nuevo stock total</FormLabel>
            <Input
              size="sm"
              type="number"
              min={0}
              mb={3}
              value={adjustQty}
              onChange={(e) => setAdjustQty(e.target.value)}
            />
            {!adjustTarget?.lots?.length && (
              <Flex gap={2} mb={3}>
                <Box flex={1}>
                  <FormLabel fontSize="sm">Zona</FormLabel>
                  <Input
                    size="sm"
                    value={adjustTarget?.zone || ''}
                    onChange={(e) =>
                      setAdjustTarget((t) => ({ ...t, zone: e.target.value }))
                    }
                  />
                </Box>
                <Box w="70px">
                  <FormLabel fontSize="sm">Repisa</FormLabel>
                  <Input
                    size="sm"
                    value={adjustTarget?.shelfLetter || ''}
                    onChange={(e) =>
                      setAdjustTarget((t) => ({ ...t, shelfLetter: e.target.value }))
                    }
                  />
                </Box>
                <Box w="80px">
                  <FormLabel fontSize="sm">Estante</FormLabel>
                  <Input
                    size="sm"
                    value={adjustTarget?.shelfNumber || ''}
                    onChange={(e) =>
                      setAdjustTarget((t) => ({ ...t, shelfNumber: e.target.value }))
                    }
                  />
                </Box>
              </Flex>
            )}
            <FormLabel fontSize="sm">
              Motivo del ajuste <Text as="span" color="red.500">*</Text>
            </FormLabel>
            <Textarea
              size="sm"
              rows={3}
              placeholder="Ej: Conteo físico, merma, error de carga…"
              value={adjustReason}
              onChange={(e) => setAdjustReason(e.target.value)}
            />
          </ModalBody>
          <ModalFooter gap={2}>
            <Button size="sm" variant="ghost" onClick={() => setAdjustTarget(null)}>
              Cancelar
            </Button>
            <Button
              size="sm"
              colorScheme="orange"
              isLoading={actionLoading}
              onClick={submitAdjust}
            >
              Guardar ajuste
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={!!batchOkCategory}
        onClose={() => setBatchOkCategory(null)}
        isCentered
        size={{ base: 'full', md: 'md' }}
        scrollBehavior="inside"
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">Marcar hoja como OK</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" mb={3}>
              Se marcarán todos los productos <strong>pendientes</strong> de la categoría «
              {batchOkCategory?.name}».
            </Text>
            <FormLabel fontSize="sm">Motivo</FormLabel>
            <Textarea
              size="sm"
              rows={2}
              value={batchOkReason}
              onChange={(e) => setBatchOkReason(e.target.value)}
            />
          </ModalBody>
          <ModalFooter gap={2}>
            <Button size="sm" variant="ghost" onClick={() => setBatchOkCategory(null)}>
              Cancelar
            </Button>
            <Button
              size="sm"
              colorScheme="green"
              isLoading={actionLoading}
              onClick={submitBatchOk}
            >
              Confirmar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ConfirmModal
        isOpen={confirmKind === 'discard'}
        onClose={() => setConfirmKind(null)}
        onConfirm={confirmDiscard}
        header="Descartar borrador"
        message="¿Descartar este borrador de control de stock? No se podrá recuperar."
        confirmLabel="Descartar"
        confirmVariant="danger"
        isLoading={saving}
      />
      <ConfirmModal
        isOpen={confirmKind === 'finish'}
        onClose={() => setConfirmKind(null)}
        onConfirm={confirmFinish}
        header="Finalizar control incompleto"
        message={
          <>
            <Text fontSize="sm" color="gray.700" mb={2}>
              Faltan{' '}
              <Text as="span" fontWeight="bold">
                {pendingFinishCount}
              </Text>{' '}
              producto(s) por revisar.
            </Text>
            <Text fontSize="sm" color="gray.600">
              ¿Desea finalizar el control igualmente? Los productos pendientes quedarán
              sin marcar en el registro.
            </Text>
          </>
        }
        confirmLabel="Finalizar igualmente"
        confirmVariant="warning"
        isLoading={saving}
      />

      <style>{`
        @media print {
          body * { visibility: hidden; }
          .stock-control-page,
          .stock-control-page * { visibility: visible; }
          .stock-control-page {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .no-print,
          .stock-control-cards,
          .chakra-tabs__tablist { display: none !important; }
          .print-all-categories { display: block !important; }
          .print-category { page-break-after: always; }
          .stock-control-table {
            display: block !important;
            max-height: none !important;
            overflow: visible !important;
          }
          .stock-control-table thead { display: table-header-group; }
        }
      `}</style>
    </>
  );
};

export default StockControlPanel;
